import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, TrendingUp } from "lucide-react";
import DashboardMockup from "@/components/DashboardMockup";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 700], [0, -60]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-28 pb-10 sm:pt-40 sm:pb-16" onMouseMove={onMouseMove}>
      <div className="bg-grid-faint pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute top-40 -left-32 h-72 w-72 rounded-full bg-turquoise/10 blur-[100px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span data-testid="hero-badge" className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-white px-4 py-1.5 text-xs font-bold text-brand shadow-soft">
            <span className="h-1.5 w-1.5 rounded-full bg-turquoise" />
            La plateforme tout-en-un des professionnels du loisir
          </span>
        </motion.div>

        <h1 className="mx-auto mt-6 max-w-4xl text-balance text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.1}>Pilotez toute</MaskedLine>
          <MaskedLine delay={0.24} className="text-brand">votre activité.</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          Réservations, planning, caisse, dépenses, flotte, équipe et statistiques.
          ActiPilote centralise votre exploitation dans une seule plateforme.
        </motion.p>

        <motion.div
          className="mt-7 flex flex-col items-center justify-center gap-3 sm:mt-8 sm:flex-row"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
        >
          <Link to="/contact" data-testid="hero-cta-button" className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700 sm:w-auto">
            Commencer avec ActiPilote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/#fonctionnalites" data-testid="hero-secondary-cta" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-bold text-navy shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/30 hover:text-brand sm:w-auto">
            Découvrir la plateforme
          </Link>
        </motion.div>

        <motion.p className="mt-5 text-xs font-medium text-navy/45" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
          Conçu pour les professionnels des activités touristiques et de loisirs.
        </motion.p>

        <motion.div style={{ y: parallaxY }} className="relative mx-auto mt-10 max-w-5xl [perspective:1400px] sm:mt-14">
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <DashboardMockup />
          </motion.div>

          <motion.div
            className="animate-float absolute -left-3 top-16 hidden rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lift sm:block lg:-left-10"
            initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.4, duration: 0.7 }}
            data-testid="hero-floating-booking"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-turquoise/10 text-turquoise"><CalendarCheck className="h-4 w-4" /></span>
              <div>
                <p className="text-xs font-bold text-navy">Nouvelle réservation</p>
                <p className="font-mono text-[11px] text-navy/50">Quad Atlas · +680 €</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="animate-float-slow absolute -right-3 bottom-14 hidden rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lift sm:block lg:-right-10"
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.55, duration: 0.7 }}
            data-testid="hero-floating-kpi"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand"><TrendingUp className="h-4 w-4" /></span>
              <div>
                <p className="text-xs font-bold text-navy">Résultat estimé</p>
                <p className="font-mono text-[11px] font-bold text-turquoise">+18% ce mois</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
